// The analytics identity the edge issues per device and returns in the
// `x-proef-identity` header of every /v1 response. It is not an owner key (the
// device id still owns the data) and it is never cleared: deleting data
// removes content, not the pseudonym the readout joins on.
import { IdentityId } from "./contract";

/** Kept by About's delete, with the device id. */
export const IDENTITY_STORAGE_KEY = "proef.identity";

function isIdentity(value: unknown): value is IdentityId {
  return IdentityId.safeParse(value).success;
}

function readStored(): IdentityId | undefined {
  try {
    const stored = window.localStorage.getItem(IDENTITY_STORAGE_KEY);
    return isIdentity(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

let current: IdentityId | undefined = typeof window === "undefined" ? undefined : readStored();
const listeners = new Set<() => void>();

export function getIdentity(): IdentityId | undefined {
  return current;
}

/** Takes a header value; anything that is not an issued id is ignored, so a proxy cannot inject one. */
export function setIdentity(value: string | null): void {
  if (!isIdentity(value) || value === current) return;
  current = value;
  try {
    window.localStorage.setItem(IDENTITY_STORAGE_KEY, value);
  } catch {
    // Storage blocked: the identity lasts for this session, which is enough to join events.
  }
  listeners.forEach((listener) => {
    listener();
  });
}

export function subscribeIdentity(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test seam only: production code never forgets the identity (a delete keeps it). */
export function resetIdentityForTests(): void {
  current = typeof window === "undefined" ? undefined : readStored();
}
